import React, { createContext, useContext, useState, useEffect } from 'react';
import { cartAPI, wishlistAPI, productsAPI } from '@/utils/api'; // Import productsAPI
import { useAuth } from './AuthContext';
import { toast } from 'sonner';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) throw new Error('useCart must be used within CartProvider');
  return context;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    // Reload data whenever user changes (Login/Logout)
    loadCart();
    loadWishlist();
  }, [user]);

  // --- 1. LOAD CART (With Full WP Data) ---
  const loadCart = async () => {
    try {
      setLoading(true);
      // A. Get the list of items from Node.js (ID, Qty, Size)
      const dbItems = await cartAPI.get();

      // B. Fetch full product details from WordPress for each item
      // B. Fetch full product details from WordPress for each item
      const fullItems = await Promise.all(
        dbItems.map(async (item) => {
          // --- FIX: Add Safety Check ---
          // If the item has no product ID (ghost item), skip the API call
          if (!item.product || !item.product.id) {
             return item; 
          }

          try {
            // Fetch fresh data from WordPress using the ID
            const fullProduct = await productsAPI.getById(item.product.id);
            return { ...item, product: fullProduct };
          } catch (err) {
            console.warn(`Failed to load product ${item.product.id} from WP`);
            return item; // Fallback to cached data if WP fails
          }
        })
      );

      setCart(fullItems);
    } catch (error) {
      console.error('Failed to load cart:', error);
    } finally {
      setLoading(false);
    }
  };

  // --- 2. LOAD WISHLIST (With Full WP Data) ---
  const loadWishlist = async () => {
    try {
      setLoading(true);
      // A. Get IDs from Node.js
      const dbItems = await wishlistAPI.get(); // Returns [{ id: 123 }, { id: 456 }]

      // B. Fetch full details from WordPress
      const fullProducts = await Promise.all(
        dbItems.map(async (item) => {
          try {
            return await productsAPI.getById(item.id);
          } catch (err) {
            return null; // Handle deleted products
          }
        })
      );

      // Filter out any nulls (products deleted from WP)
      setWishlist(fullProducts.filter(p => p !== null));
    } catch (error) {
      console.error('Failed to load wishlist:', error);
    } finally {
      setLoading(false);
    }
  };

  const addToCart = async (product, size, color, quantity = 1) => {
    try {
      await cartAPI.add({
        product_id: product.id,
        quantity,
        size,
        color,
        // We still pass this for the initial cache, even if we fetch fresh later
        product: { name: product.name, price: product.price, images: product.images }
      });
      toast.success("Added to Cart");
      await loadCart(); // Refresh
    } catch (error) {
      toast.error("Failed to add to cart");
      throw error;
    }
  };

  const updateCart = async (itemId, quantity) => {
    try {
      await cartAPI.update({ item_id: itemId, quantity });
      // Optimistic update for speed
      setCart(prev => prev.map(item => item.id === itemId ? { ...item, quantity } : item));
    } catch (error) {
      toast.error("Failed to update");
    }
  };

  const removeFromCart = async (itemId) => {
    try {
      await cartAPI.remove(itemId);
      setCart(prev => prev.filter(item => item.id !== itemId));
      toast.success("Item removed from cart.");
    } catch (error) {
      toast.error("Failed to remove");
    }
  };

  const toggleWishlist = async (product) => {
    try {
      // FIX: Send ONLY 'product.id' to the API, not the whole object
      await wishlistAPI.toggle(product.id);

      const exists = wishlist.find(p => p.id === product.id);
      if (exists) {
        setWishlist(prev => prev.filter(p => p.id !== product.id));
      } else {
        // We keep the full object in local state for instant UI update
        setWishlist(prev => [...prev, product]);
      }
    } catch (error) {
      console.error("Wishlist toggle error:", error);
      throw error;
    }
  };

  const isInWishlist = (productId) => {
    return wishlist.some((item) => String(item.id) === String(productId));
  };

  const cartTotal = cart.reduce((sum, item) => {
    // Use the price from the FRESH product data
    const price = item.product?.price || 0;
    const discount = item.product?.discount || 0;
    const finalPrice = price * (1 - discount / 100);
    return sum + finalPrice * item.quantity;
  }, 0);

  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <CartContext.Provider
      value={{
        cart,
        wishlist,
        cartTotal,
        cartCount,
        loading,
        addToCart,
        updateCart,
        removeFromCart,
        toggleWishlist,
        isInWishlist,
        loadCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};